/**
 * Runs `apply` only while the widget's host custom element is connected to the
 * document.
 *
 * The widget is a Vue `defineCustomElement` app that renders into the host
 * element's shadow root. The bootstrap performs ~2s of network calls before
 * flipping the widget into its "ready to render" state. If the host page detaches
 * the element during that window (e.g. WordPress/SPA AJAX navigation, iOS
 * bfcache), flipping the state would trigger Vue to patch into a detached shadow
 * root, throwing `parent.insertBefore(child, anchor)` with a null parent — the
 * high-volume Sentry "insertBefore" crash cluster.
 *
 * Behaviour:
 * - Host connected now → apply immediately.
 * - Host detached now → defer until it is (re)connected, then apply once.
 * - Never reconnected within `timeoutMs` → give up (no dangling observer/leak).
 *
 * @param host - The widget's host custom element.
 * @param apply - The state mutation that starts/updates rendering.
 * @param timeoutMs - Max time to wait for reconnection before giving up.
 * @returns A disposer that cancels a pending deferral (no-op once applied).
 */
export const applyWhenConnected = (
    host: HTMLElement,
    apply: () => void,
    timeoutMs = 10000,
): (() => void) => {
    if (host.isConnected) {
        apply();
        return () => {};
    }

    let observer: MutationObserver | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const dispose = () => {
        observer?.disconnect();
        observer = undefined;
        if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
            timeoutId = undefined;
        }
    };

    observer = new MutationObserver(() => {
        if (host.isConnected) {
            dispose();
            apply();
        }
    });
    observer.observe(document, { childList: true, subtree: true });
    timeoutId = setTimeout(dispose, timeoutMs);

    return dispose;
};
